/**
 * 建立訪談時的附件 staging UI：只收集檔案、不實際上傳。
 * 父元件在訪談建立成功後，依序呼叫 uploadAttachment 送到新訪談 id。
 *
 * REQ-20260418013。
 */
import { useRef, useState } from 'react';

export const MAX_BYTES = 10 * 1024 * 1024;
// 與 AttachmentUploader.tsx 一致；後端仍為最終白名單
export const ALLOWED_EXT = /\.(pdf|docx?|xlsx?|pptx?|jpe?g|png|webp|csv|txt|zip)$/i;

export function validateFile(f: File): string | null {
  if (f.size > MAX_BYTES) return '檔案超過 10MB';
  if (!ALLOWED_EXT.test(f.name)) return '不支援此檔案類型';
  return null;
}

interface Props {
  files: File[];
  onChange: (next: File[]) => void;
  /** 觸發上傳後，用來顯示即時進度（檔名→%；done→100；error→字串） */
  progress?: Record<string, number | 'done' | string>;
  disabled?: boolean;
}

export function PendingAttachmentsPicker({
  files,
  onChange,
  progress,
  disabled,
}: Props): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const addFiles = (incoming: FileList | File[]): void => {
    const list = Array.from(incoming);
    const nextErrors = { ...errors };
    const accepted: File[] = [];
    for (const f of list) {
      const err = validateFile(f);
      if (err) {
        nextErrors[f.name] = err;
        continue;
      }
      if (files.some((x) => x.name === f.name && x.size === f.size)) continue;
      accepted.push(f);
    }
    setErrors(nextErrors);
    if (accepted.length) onChange([...files, ...accepted]);
  };

  const removeFile = (name: string, size: number): void => {
    onChange(files.filter((f) => !(f.name === name && f.size === size)));
  };

  return (
    <div className="grid gap-2">
      <div
        role="button"
        tabIndex={0}
        aria-label="附件選擇區"
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) inputRef.current?.click();
        }}
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
        }}
        className={`text-sm text-center px-3 py-2 border-2 border-dashed rounded ${
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        } ${
          dragOver
            ? 'border-blue-500 bg-blue-50'
            : 'border-slate-300 bg-slate-50 hover:bg-slate-100'
        }`}
      >
        拖放檔案到此或點擊選檔（單檔最大 10MB；PDF / Office / 圖片 / zip）
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        aria-label="附件檔案選擇"
        className="hidden"
        onChange={(e) => {
          if (e.target.files) addFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {files.length > 0 ? (
        <ul className="grid gap-1 text-xs">
          {files.map((f) => {
            const p = progress?.[f.name];
            return (
              <li key={`${f.name}-${f.size}`} className="flex items-center gap-2">
                <span className="truncate max-w-[20rem]">{f.name}</span>
                <span className="text-slate-400">
                  ({(f.size / 1024).toFixed(1)} KB)
                </span>
                {p === undefined ? (
                  disabled ? null : (
                    <button
                      type="button"
                      onClick={() => removeFile(f.name, f.size)}
                      className="text-red-600 hover:underline"
                      aria-label={`移除 ${f.name}`}
                    >
                      移除
                    </button>
                  )
                ) : p === 'done' ? (
                  <span className="text-green-600">✓</span>
                ) : typeof p === 'number' ? (
                  <span className="text-slate-500">{p}%</span>
                ) : (
                  <span role="alert" className="text-red-600">
                    {p}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}

      {Object.entries(errors).length > 0 ? (
        <ul role="alert" className="text-xs text-red-600 grid gap-0.5">
          {Object.entries(errors).map(([name, msg]) => (
            <li key={name}>
              {name}：{msg}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
